import { Button } from '@/components/ui/button'
import { HttpError } from '@/api/core'
import { Link, useRouter } from '@tanstack/react-router'
import { AlertTriangle, Home, RefreshCw, SearchX } from 'lucide-react'
import { useTranslation } from 'react-i18next'

interface Props {
  error?: unknown
  reset?: () => void
}

export function ErrorScreen({ error, reset }: Props) {
  const { t } = useTranslation('common')
  const router = useRouter()

  const status = error instanceof HttpError ? error.status : 0
  const code = error instanceof HttpError ? error.code : null
  const message =
    error instanceof HttpError
      ? error.message
      : error instanceof Error
        ? error.message
        : null

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-4 py-16 text-center">
      <div className="rounded-full bg-destructive/10 p-4">
        <AlertTriangle className="h-8 w-8 text-destructive" />
      </div>
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-semibold text-foreground">
          {t('error_screen.title', 'Une erreur est survenue')}
        </h2>
        {message ? (
          <p className="max-w-md text-sm text-muted-foreground">{message}</p>
        ) : (
          <p className="max-w-md text-sm text-muted-foreground">
            {t('error_screen.generic', "L'opération n'a pas pu aboutir. Réessayez ou revenez au tableau de bord.")}
          </p>
        )}
        {code ? (
          <p className="mt-1 font-mono text-xs text-muted-foreground/70">
            {code}{status ? ` · ${status}` : ''}
          </p>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (reset) {
              reset()
            } else {
              void router.invalidate()
            }
          }}
        >
          <RefreshCw className="h-4 w-4" />
          {t('error_screen.retry', 'Réessayer')}
        </Button>
        <Link to="/dashboard">
          <Button variant="ghost" size="sm">
            <Home className="h-4 w-4" />
            {t('error_screen.home', 'Tableau de bord')}
          </Button>
        </Link>
      </div>
    </div>
  )
}

export function NotFoundScreen() {
  const { t } = useTranslation('common')
  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-4 py-16 text-center">
      <div className="rounded-full bg-muted p-4">
        <SearchX className="h-8 w-8 text-muted-foreground" />
      </div>
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-semibold text-foreground">
          {t('error_screen.not_found_title', 'Page introuvable')}
        </h2>
        <p className="max-w-md text-sm text-muted-foreground">
          {t('error_screen.not_found', "Cette page n'existe pas ou a été déplacée.")}
        </p>
      </div>
      <Link to="/dashboard">
        <Button variant="outline" size="sm">
          <Home className="h-4 w-4" />
          {t('error_screen.home', 'Tableau de bord')}
        </Button>
      </Link>
    </div>
  )
}
